'use client';

import { resolveNumber, resolvePath } from '@macaron-a2ui/core/v0_8';
import type { MacaronRenderContext, RecordAny } from '@macaron-a2ui/core/v0_8';

const TICK_DIVISIONS = 5;

function normalizeMax(value: number) {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function clampTickIndex(index: number) {
  return Math.max(0, Math.min(TICK_DIVISIONS, Math.round(index)));
}

function snapValue(value: number, max: number) {
  const normalizedMax = normalizeMax(max);
  if (normalizedMax <= 0) return { snappedValue: 0, tickIndex: 0 };
  const step = normalizedMax / TICK_DIVISIONS;
  const tickIndex = clampTickIndex(value / step);
  return { snappedValue: Number((step * tickIndex).toFixed(6)), tickIndex };
}

function formatTickValue(value: number) {
  if (!Number.isFinite(value)) return '0';
  const normalized = Number(value.toFixed(2));
  return Number.isInteger(normalized) ? String(normalized) : normalized.toString();
}

export function MacaronTickSlider({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const valuePath = resolvePath(config.value);
  const max = normalizeMax(resolveNumber(config.max, ctx.data));
  const { snappedValue, tickIndex } = snapValue(resolveNumber(config.value, ctx.data), max);

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="w-6 shrink-0 text-xs text-[#667085]">0</span>
        <input
          type="range"
          min={0}
          max={TICK_DIVISIONS}
          step={1}
          value={tickIndex}
          className="w-full"
          disabled={!valuePath || max <= 0}
          onChange={(e) => {
            if (!valuePath || max <= 0) return;
            const nextTickIndex = clampTickIndex(Number(e.target.value));
            const nextValue = Number(((max / TICK_DIVISIONS) * nextTickIndex).toFixed(6));
            ctx.updateData?.(valuePath, nextValue);
          }}
        />
        <span className="min-w-10 text-right text-xs text-[#667085]">{formatTickValue(max)}</span>
      </div>
      <div className="text-right text-xs text-[#667085]">{formatTickValue(snappedValue)}</div>
    </div>
  );
}
